import { useCallback, useEffect } from 'react';

import { Modal, ModalBody, ModalHeader, Text } from '@change/ds-core-ui';
import { CheckmarkVerifiedIcon } from '@change/ds-icons';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { useSignatureTracking, useSignFormStep } from '#src/app/pages/petitionDetails/shared/hooks';

import { useMobileSignFormValues } from '../../hooks/mobileSignFormContext/useMobileSignFormValues';
import { SignFormContextProvider } from '../../hooks/signFormContext';
import { preloadSignForm, SignFormContainer } from '../signFormSunrise';

import { RecentSignersFeed } from './components/RecentSignersFeed';
import type { ActionPanelSunriseVariant } from './MobileActionButton';
import { MobileModalContextDecorator } from './shared/MobileModalContextDecorator';
import type { MobileModalContext } from './shared/types';

const PRELOAD_SIGN_FORM_DELAY = 5000; // 5s

const MobileSignModalInner = ({
	isOpen,
	setIsOpen,
	variant,
}: {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	variant: ActionPanelSunriseVariant;
}) => {
	const { translate } = useI18n();
	const { mobileSignFormValues, updateMobileSignFormValues } = useMobileSignFormValues();

	const { trackClosedModal } = useSignatureTracking();

	const { signFormStep, setSignFormStep } = useSignFormStep();

	const onClose = useCallback(() => {
		setSignFormStep(1);
		trackClosedModal();
	}, [setSignFormStep, trackClosedModal]);

	const variantProps = {
		v1: {
			fullScreenOnMobile: true,
			enableTransitionAnimations: false,
		},
		v2: {
			fullScreenOnMobile: false,
			enableTransitionAnimations: true,
		},
	};

	return (
		<Modal
			size="large"
			isOpen={isOpen}
			onClose={onClose}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			onOpenChange={setIsOpen}
			data-qa="sign-form-modal"
			// This is to avoid the sticky action button to be on top of the modal
			className="z-5000"
			{...variantProps[variant]}
		>
			{signFormStep === 1 && (
				<ModalHeader
					title={
						<Text variant="heading" size="x-small" fontWeight="bold">
							<Translate value="fe.components.petition_sign.complete_signature" />
						</Text>
					}
				/>
			)}
			{signFormStep === 2 && (
				<ModalHeader
					title={
						<Text variant="heading" size="x-small" fontWeight="bold">
							<Translate value="corgi.petition_gamma.petition_sign.address.verify_title" />
						</Text>
					}
					subtitle={<Translate value="corgi.petition_gamma.petition_sign.address.disclaimer" />}
					leadingSlot={<CheckmarkVerifiedIcon data-testid="modal-header-checkmark-icon" />}
					leadingSlotClassName="text-icon-information"
				/>
			)}
			<ModalBody>
				<div className="w-full px-1 py-1" data-qa="sign-form-modal-container">
					<SignFormContextProvider insideMobileModal>
						{signFormStep === 1 && <RecentSignersFeed />}

						<SignFormContainer
							previousState={mobileSignFormValues}
							onChange={updateMobileSignFormValues}
							dataQa="sign-form-mobile"
						/>
					</SignFormContextProvider>
				</div>
			</ModalBody>
		</Modal>
	);
};

export const MobileSignModal = ({
	context,
	isOpen,
	setIsOpen,
	variant,
}: {
	context: MobileModalContext;
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
	variant: ActionPanelSunriseVariant;
}) => {
	return (
		<MobileModalContextDecorator {...context}>
			<MobileSignModalInner isOpen={isOpen} setIsOpen={setIsOpen} variant={variant} />
		</MobileModalContextDecorator>
	);
};

export function usePreloadMobileSignModal(): void {
	// preload the sign form chunk after a few seconds
	useEffect(() => {
		const timeout = window.setTimeout(() => {
			void preloadSignForm();
		}, PRELOAD_SIGN_FORM_DELAY);
		return () => window.clearTimeout(timeout);
	}, []);
}
